// src/pages/UsersAdmin.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as TechIcon,
  Visibility as SupervisorIcon
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';
import { useNotifications } from '../context/NotificationContext';

export default function UsersAdmin() {
  const theme = useTheme();
  const { showNotification } = useNotifications();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('operator');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const storedUsers = await getData('users') || [];
        setUsers(storedUsers);
      } catch (error) {
        console.error("Failed to load users:", error);
        showNotification('Error al cargar la lista de usuarios', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, [showNotification]);

  const resetForm = () => {
    setEditingUser(null);
    setUsername('');
    setPassword('');
    setRole('operator');
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <AdminIcon />;
      case 'technician': return <TechIcon />;
      case 'supervisor': return <SupervisorIcon />;
      default: return <PersonIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'technician': return 'warning';
      case 'supervisor': return 'info';
      default: return 'primary';
    }
  };

  const saveUser = async () => {
    if (!username.trim()) {
      showNotification('Debe ingresar un nombre de usuario', 'warning');
      return;
    }

    let updatedUsers = [...users];
    
    if (editingUser) {
      // Редагування існуючого користувача
      const index = updatedUsers.findIndex(u => u.id === editingUser.id);
      if (index !== -1) {
        // Перевірка унікальності імені (крім поточного користувача)
        if (users.some(u => u.id !== editingUser.id && u.username === username)) {
          showNotification('El nombre de usuario ya está en uso', 'error');
          return;
        }
        
        updatedUsers[index] = {
          ...updatedUsers[index],
          username: username.trim(),
          password: password ? password : updatedUsers[index].password,
          role,
          updatedAt: new Date().toISOString()
        };
      }
    } else {
      // Створення нового користувача
      if (users.some(u => u.username === username.trim())) {
        showNotification('El usuario ya existe', 'error');
        return;
      }
      
      updatedUsers.push({ 
        id: Date.now(), 
        username: username.trim(), 
        password, 
        role,
        createdAt: new Date().toISOString()
      });
    }
    
    try {
      await saveData('users', updatedUsers);
      setUsers(updatedUsers);
      resetForm();
      showNotification(
        editingUser ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente', 
        'success'
      );
    } catch (error) {
      console.error("Failed to save user:", error);
      showNotification('Error al guardar los datos del usuario', 'error');
    }
  };

  const editUser = (user) => {
    setEditingUser(user);
    setUsername(user.username);
    setPassword('');
    setRole(user.role);
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const filtered = users.filter(u => u.id !== userToDelete.id);
      await saveData('users', filtered);
      setUsers(filtered);
      showNotification('Usuario eliminado correctamente', 'success');
      if (editingUser && editingUser.id === userToDelete.id) resetForm();
    } catch (error) {
      console.error("Failed to delete user:", error);
      showNotification('Error al eliminar el usuario', 'error');
    } finally {
      closeDeleteDialog();
    }
  };

  const handleBackup = async () => {
    if (!window.electronAPI) {
      showNotification("Esta función solo está disponible en la versión de escritorio.", 'info');
      return;
    }
    const result = await window.electronAPI.backupData();
    if (result.success) {
      showNotification(`Copia de seguridad creada: ${result.path}`, 'success');
    } else if (result.reason !== 'canceled') {
      showNotification(`Error al crear la copia: ${result.error}`, 'error');
    }
  };

  const handleRestore = async () => {
    if (!window.confirm("¡ATENCIÓN! Esto sobrescribirá TODOS los datos actuales. ¿Desea continuar?")) return;
    if (!window.electronAPI) {
      showNotification("Esta función solo está disponible en la versión de escritorio.", 'info');
      return;
    }
    const result = await window.electronAPI.restoreData();
    if (result.success) {
      showNotification('Datos restaurados. Por favor, reinicie la aplicación.', 'success');
    } else if (result.reason !== 'canceled') {
      showNotification(`Error al restaurar los datos: ${result.error}`, 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: theme.palette.primary.main,
            mx: 'auto',
            mb: 2
          }}
        >
          <AdminIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Administrar Usuarios
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gestión de usuarios del sistema y copias de seguridad
        </Typography>
      </Box>

      {/* Секція резервних копій */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BackupIcon color="primary" />
            Copia de Seguridad y Restauración
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Cree una copia de seguridad de todos los datos de la aplicación o restaure desde un archivo existente.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              startIcon={<BackupIcon />} 
              onClick={handleBackup}
              sx={{ borderRadius: 2 }}
            >
              Crear Copia de Seguridad
            </Button>
            <Button 
              variant="outlined" 
              color="warning" 
              startIcon={<RestoreIcon />} 
              onClick={handleRestore}
              sx={{ borderRadius: 2 }}
            >
              Restaurar Datos
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Форма створення/редагування користувача */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingUser ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
            {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
          </Typography>

          <Box component="form" onSubmit={e => { e.preventDefault(); saveUser(); }} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField 
                label="Nombre de usuario" 
                variant="outlined" 
                size="small" 
                value={username} 
                onChange={e => setUsername(e.target.value)} 
                required 
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField 
                label="Contraseña (dejar en blanco para no cambiar)" 
                type="password" 
                variant="outlined" 
                size="small" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                <InputLabel>Rol</InputLabel>
                <Select value={role} label="Rol" onChange={e => setRole(e.target.value)}>
                  <MenuItem value="operator">Operador</MenuItem>
                  <MenuItem value="technician">Técnico</MenuItem>
                  <MenuItem value="supervisor">Supervisor</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                type="submit" 
                variant="contained" 
                startIcon={editingUser ? <SaveIcon /> : <AddIcon />}
                sx={{ borderRadius: 2 }}
              >
                {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
              </Button>
              {editingUser && (
                <Button onClick={resetForm} variant="outlined" sx={{ borderRadius: 2 }}>
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Таблиця користувачів */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Lista de Usuarios ({users.length})
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Fecha de Creación</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => (
                  <TableRow 
                    key={user.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: theme.palette.action.hover 
                      } 
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                          {getRoleIcon(user.role)}
                        </Avatar>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        icon={getRoleIcon(user.role)}
                        label={user.role} 
                        color={getRoleColor(user.role)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        onClick={() => editUser(user)} 
                        color="primary"
                        sx={{ '&:hover': { backgroundColor: theme.palette.primary.light } }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => openDeleteDialog(user)} 
                        color="error"
                        sx={{ '&:hover': { backgroundColor: theme.palette.error.light } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No hay usuarios registrados
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог підтвердження видалення */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar al usuario <strong>{userToDelete?.username}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button 
            onClick={deleteUser} 
            variant="contained" 
            color="error" 
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            Eliminar Usuario
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


